import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { PageEvent } from '@angular/material/paginator';
import { MatDialog } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { StockyEntryService } from '../../services/stocky-entry.service';
import { RemoveWaybillItemDialogComponent } from '../../dialogs/remove-waybill-item-dialog.component';

export interface WaybillItem {
  id: string;
  idTrade: string;
  numeroNota: number;
  chaveNota: string;
  status: string;
  checkList: string;
  motivoRecusa?: string | null;
}

@Component({
  selector: 'app-receiving-waybill',
  templateUrl: './receiving-waybill.component.html',
  styleUrls: ['./receiving-waybill.component.sass'],
})
export class ReceivingWaybillComponent implements OnInit {
  filterForm: FormGroup;

  lots: { id: string; number: string }[] = [];
  lotSearch = '';
  items: WaybillItem[] = [];

  totalItems = 0;
  currentPage = 1;
  pageSize = 10;

  stats = { checkedOk: 0, pendingReading: 0, notListed: 0 };

  displayedColumns = ['idTrade', 'nfe', 'chaveNfe', 'status', 'physicalChecklist', 'actions'];

  constructor(
    private fb: FormBuilder,
    private dialog: MatDialog,
    private service: StockyEntryService,
    private toast: ToastrService
  ) {}

  ngOnInit(): void {
    this.filterForm = this.fb.group({
      lotId: ['', Validators.required],
      deliveryDate: [null, Validators.required],
      sheetNumber: ['', [Validators.required, Validators.pattern(/^\d+$/)]],
    });

    this.loadLots();
  }

  loadLots(): void {
    this.service.getLotsCombobox().subscribe({
      next: (response) => {
        this.lots = (response ?? []).map((item: any) => ({
          id: item.id,
          number: item.numero,
        }));
      },
    });
  }

  get filteredLots(): { id: string; number: string }[] {
    const searchTerm = this.lotSearch.trim();

    if (!searchTerm) {
      return this.lots;
    }

    return this.lots.filter((lot) => String(lot.number).includes(searchTerm));
  }

  onLotChange(): void {
    this.currentPage = 1;
    this.loadItems();
  }

  loadItems(): void {
    const { lotId } = this.filterForm.value;
    if (!lotId) return;

    this.service
      .getWaybillItems(lotId, { Page: this.currentPage, PageSize: this.pageSize })
      .subscribe({
        next: (response) => {
          this.items = response?.itens?.page ?? [];
          this.totalItems = response?.itens?.count ?? 0;
          this.stats = {
            checkedOk: response?.itensConferidos ?? 0,
            pendingReading: response?.aguardandoLeitura ?? 0,
            notListed: response?.itensFora ?? 0,
          };
        },
      });
  }

  handlePageEvent(e: PageEvent): void {
    this.pageSize = e.pageSize;
    this.currentPage = e.pageIndex + 1;
    this.loadItems();
  }

  printWaybill(): void {
    const { lotId, sheetNumber } = this.filterForm.value;

    this.service.printWaybill(lotId, sheetNumber).subscribe({
      next: (blob: Blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.target = '_blank';
        link.click();
        URL.revokeObjectURL(url);
      },
      error: (err) => {
        this.toast.error(err.errors[0] ?? 'Erro ao imprimir o romaneio.');
      },
    });
  }

  closeLot(): void {
    const { lotId, deliveryDate, sheetNumber } = this.filterForm.value;

    const payload = {
      numeroFolha: Number(sheetNumber) || 0,
      dataEntrega: new Date(deliveryDate).toISOString(),
    };

    this.service.closeLot(lotId, payload).subscribe({
      next: () => {
        this.toast.success('Lote fechado com sucesso.');
        this.filterForm.reset({ lotId: '', deliveryDate: null, sheetNumber: '' });
        this.lotSearch = '';
        this.items = [];
        this.stats = { checkedOk: 0, pendingReading: 0, notListed: 0 };
        this.loadLots();
      },
      error: (err) => {
        this.toast.error(err.errors[0] ?? 'Erro ao fechar o lote.');
      },
    });
  }

  removeItem(item: WaybillItem): void {
    const dialogRef = this.dialog.open(RemoveWaybillItemDialogComponent, {
      disableClose: true,
      width: '700px',
      data: { idTrade: item.idTrade },
    });

    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (!confirmed) return;

      this.service.removeWaybillItem(item.id).subscribe({
        next: () => {
          this.toast.success('Item removido do lote com sucesso.');
          this.loadItems();
        },
        error: (err) => {
          this.toast.error(err.errors[0] ?? 'Erro ao remover o item do lote.');
        },
      });
    });
  }

  getStatus(item: WaybillItem): string {
    return item.status ?? '';
  }

  get hasLot(): boolean {
    return !!this.filterForm?.get('lotId')?.value;
  }

  statusClass(status: string): string {
    const map: Record<string, string> = {
      'Conferência OK': 'status-ok',
      'Aguardando leitura': 'status-pending-reading',
      'Aguardando': 'status-pending',
      'Fora da lista': 'status-not-listed',
      'Com Divergência': 'status-not-listed',
      'Recebimento Recusado': 'status-refused',
    };
    return map[status] ?? '';
  }
}
